// @ts-nocheck
import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useOrgModules } from "@/contexts/OrgModulesContext";
import { useAuth } from "@/contexts/AuthContext";

const ModulesSettings: React.FC = () => {
  const { profile } = useAuth();
  const { modules, isLoading, setModule, orgId } = useOrgModules();
  const [saving, setSaving] = useState<string | null>(null);

  const isOrgAdmin = profile?.role === "admin" || profile?.role === "org_admin";

  const grouped = useMemo(() => {
    const byCat: Record<string, any[]> = {};
    [...modules]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach((m) => {
        const cat = m.category || "Other";
        (byCat[cat] ||= []).push(m);
      });
    return Object.entries(byCat);
  }, [modules]);

  if (!isOrgAdmin) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Only organization administrators can manage modules.
        </CardContent>
      </Card>
    );
  }

  const handleToggle = async (key: string, label: string, enabled: boolean) => {
    setSaving(key);
    try {
      await setModule(key, enabled);
      toast.success(`${label} ${enabled ? "enabled" : "disabled"}`);
    } catch (e: any) {
      toast.error(e?.message || "Could not update this module");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary-700">Modules</h1>
        <p className="text-muted-foreground">
          Turn features on or off for your organization. Disabled modules disappear from the
          menu and their pages become unavailable.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !orgId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No organization selected.
          </CardContent>
        </Card>
      ) : (
        grouped.map(([category, items]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle>{category}</CardTitle>
            </CardHeader>
            <CardContent className="divide-y">
              {items.map((m) => (
                <div key={m.module_key} className="flex items-start justify-between gap-4 py-4">
                  <div className="min-w-0">
                    <p className="font-medium">{m.label}</p>
                    {m.description && (
                      <p className="text-sm text-muted-foreground">{m.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {saving === m.module_key && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    <Switch
                      checked={!!m.is_enabled}
                      disabled={saving === m.module_key}
                      onCheckedChange={(v) => handleToggle(m.module_key, m.label, v)}
                      aria-label={`Toggle ${m.label}`}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default ModulesSettings;
